import axios from 'axios'
import queryString from 'query-string'
import { VirtualCardSecureResponse } from 'src/models/virtualCard.models'
import { getResponseData } from 'src/queries/network.utils'
import { getRequestConfig, RequestConfig } from 'src/queries/requestConfig'
import {
  PatchBulkVirtualCardUpdateResponse,
  PostBulkVirtualCardResponse,
  PutBulkVirtualCardCloseResponse,
  PutBulkVirtualCardDeactivateResponse,
  PutVirtualCardCancelResponse,
  PutVirtualCardCancelUpdateResponse,
  VirtualCardsFiltersBody,
  VirtualCardsResponse,
} from 'src/queries/virtualCards.models'
import { PatchBulkVirtualCardUpdateRequest } from 'src/requests/patchBulkVirtualCardUpdate.request'
import { PostBulkVirtualCardRequest } from 'src/requests/postBulkVirtualCard.request'

// Imperative single-card read against the VGS vault, for the Pusher sync path.
// Mirrors the endpoint the detail query (useQueryVirtualCardDetail) uses, so a
// fetched card is a drop-in for the detail + list caches. This is the
// rate-limited upstream read — callers must gate it (see pusherEntitySync).
export const getVirtualCardDetail = async (
  virtualCardId: string,
): Promise<VirtualCardSecureResponse> => {
  const { virtualCard } = await getResponseData(
    axios.get<{ virtualCard: VirtualCardSecureResponse }>(
      `/virtualcards/${virtualCardId}`,
      getRequestConfig(RequestConfig.Vault),
    ),
  )
  return virtualCard
}

export const getVirtualCards = async ({
  filters,
  page,
  count,
}: {
  filters: VirtualCardsFiltersBody | undefined
  page: number
  count: number
}): Promise<VirtualCardsResponse> => {
  return getResponseData(
    axios.get<VirtualCardsResponse>(
      `virtualcards?${queryString.stringify({ ...filters, page, count })}`,
      getRequestConfig(RequestConfig.Default),
    ),
  )
}

// Creates virtual cards in bulk (one or more recipients). Replaces the write
// path of the legacy `postBulkVirtualCardAndOpenToastAndCloseFlyout` thunk.
export const postBulkVirtualCard = async (
  body: Partial<PostBulkVirtualCardRequest>,
): Promise<PostBulkVirtualCardResponse> => {
  return getResponseData(
    axios.post<PostBulkVirtualCardResponse>(
      `/virtualcards/bulk`,
      body,
      getRequestConfig(RequestConfig.Default),
    ),
  )
}

// Bulk-updates virtual cards (notes/balance/validTo/recurrence). Replaces the
// write path of the legacy `patchBulkVirtualCardUpdateAndCloseModal` thunk.
export const patchBulkVirtualCardUpdate = async (
  body: PatchBulkVirtualCardUpdateRequest,
): Promise<PatchBulkVirtualCardUpdateResponse> => {
  return getResponseData(
    axios.patch<PatchBulkVirtualCardUpdateResponse>(
      `/virtualcards/bulkupdate`,
      body,
      getRequestConfig(RequestConfig.Default),
    ),
  )
}

// Bulk-deactivates virtual cards. Replaces the write path of the legacy
// `putBulkVirtualCardDeactivateAndCloseModal` thunk.
export const putBulkVirtualCardDeactivate = async (
  virtualCardIds: string[],
): Promise<PutBulkVirtualCardDeactivateResponse> => {
  return getResponseData(
    axios.put<PutBulkVirtualCardDeactivateResponse>(
      `/virtualcards/bulkdeactivate`,
      virtualCardIds,
      getRequestConfig(RequestConfig.Default),
    ),
  )
}

// Bulk-closes virtual cards. Replaces the write path of the legacy
// `putBulkVirtualCardCloseAndCloseModal` thunk.
export const putBulkVirtualCardClose = async (
  virtualCardIds: string[],
): Promise<PutBulkVirtualCardCloseResponse> => {
  return getResponseData(
    axios.put<PutBulkVirtualCardCloseResponse>(
      `/virtualcards/bulkclose`,
      virtualCardIds,
      getRequestConfig(RequestConfig.Default),
    ),
  )
}

// Cancels a virtual card (a pending request, or an active card). Replaces the
// write path of the legacy `putVirtualCardCancelAndCloseModalOrRedirect`
// thunk.
export const putVirtualCardCancel = async (
  id: string,
): Promise<PutVirtualCardCancelResponse> => {
  return getResponseData(
    axios.put<PutVirtualCardCancelResponse>(
      `/virtualcards/${id}/cancel`,
      undefined,
      getRequestConfig(RequestConfig.Default),
    ),
  )
}

// Cancels a pending virtual card update request. Replaces the write path of
// the legacy `putVirtualCardCancelUpdateAndOpenToastAndCloseModal` thunk.
export const putVirtualCardCancelUpdate = async (
  id: string,
): Promise<PutVirtualCardCancelUpdateResponse> => {
  return getResponseData(
    axios.put<PutVirtualCardCancelUpdateResponse>(
      `/virtualcards/${id}/cancelupdate`,
      undefined,
      getRequestConfig(RequestConfig.Default),
    ),
  )
}
